// Two-Factor Authentication (TOTP) — Supabase Auth built-in MFA.
// Called from Settings → Security tab. Login challenge lives in login.js.
//
// Uses the shared supabase client from config.js (set by app.js at
// boot) instead of creating its own. Two separate clients sharing the
// same auth-token storage key trigger Supabase's
// "Multiple GoTrueClient instances detected" warning and risk
// undefined behaviour during concurrent auth ops.

import { getSupabase } from './config.js';

// Use current origin so this works on any domain alias (app.fitopspro.com,
// localhost, preview deploys). Falls back for unusual cases.
const FN_BASE = (typeof window !== 'undefined' && window.location?.origin)
  ? `${window.location.origin}/.netlify/functions`
  : 'https://app.fitopspro.com/.netlify/functions';

async function authToken() {
  const { data: { session } } = await getSupabase().auth.getSession();
  return session?.access_token || null;
}

// ─── Backup codes ─────────────────────────────────────────────
async function generateBackupCodes() {
  const token = await authToken();
  const res = await fetch(`${FN_BASE}/mfa-backup-generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to generate codes');
  return (await res.json()).codes;
}

async function fetchBackupCount() {
  const token = await authToken();
  const res = await fetch(`${FN_BASE}/mfa-backup-count`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return 0;
  return (await res.json()).remaining || 0;
}

function showBackupCodesModal(codes) {
  const modal = document.getElementById('mfa-codes-modal');
  const list = document.getElementById('mfa-codes-list');
  const ack = document.getElementById('mfa-codes-ack');
  const doneBtn = document.getElementById('mfa-codes-done');
  list.innerHTML = codes.map(c => `<span style="color:#fafafa;">${c}</span>`).join('');
  ack.checked = false;
  doneBtn.disabled = true;
  modal.classList.remove('hidden');

  ack.onchange = () => { doneBtn.disabled = !ack.checked; };
  doneBtn.onclick = () => { modal.classList.add('hidden'); renderMfaStatus(); };

  document.getElementById('mfa-codes-copy').onclick = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    document.getElementById('mfa-codes-copy').textContent = 'Copied!';
    setTimeout(() => { document.getElementById('mfa-codes-copy').textContent = 'Copy all'; }, 1500);
  };
  document.getElementById('mfa-codes-download').onclick = () => {
    const blob = new Blob(
      [`FitOps Pro — MFA Backup Codes\n\nGenerated: ${new Date().toISOString()}\n\n${codes.join('\n')}\n\nEach code works once. Store securely.`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'fitops-pro-mfa-backup-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };
}

// ─── UI: render the Security panel ────────────────────────────
async function renderMfaStatus() {
  const host = document.getElementById('mfa-status');
  if (!host) return;
  try {
    const { data, error } = await getSupabase().auth.mfa.listFactors();
    if (error) throw error;
    const verified = (data?.totp || []).filter(f => f.status === 'verified');
    if (verified.length > 0) {
      const f = verified[0];
      const enrolledAt = f.created_at ? new Date(f.created_at).toLocaleDateString() : '—';
      const backupCount = await fetchBackupCount();
      const backupWarning = backupCount <= 2 ? `<div style="font-size:12px;color:#eab308;margin-top:8px;">⚠️ Only ${backupCount} backup code${backupCount === 1 ? '' : 's'} left — regenerate to get 10 new ones.</div>` : '';
      host.innerHTML = `
        <div style="padding:12px;border:1px solid #10b981;background:rgba(16,185,129,0.08);border-radius:8px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:#10b981;margin-bottom:4px;">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M5 10l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Two-Factor Authentication is enabled
          </div>
          <div style="font-size:12px;color:#a1a1aa;">Enrolled on ${enrolledAt} · ${backupCount} backup codes remaining</div>
          ${backupWarning}
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn-secondary" id="btn-mfa-regen">Regenerate backup codes</button>
          <button class="btn-secondary" id="btn-mfa-disable" style="color:#dc2626;border-color:#dc2626;">Disable 2FA</button>
        </div>
      `;
      document.getElementById('btn-mfa-disable').onclick = () => openDisableModal(f.id);
      document.getElementById('btn-mfa-regen').onclick = async () => {
        try {
          const codes = await generateBackupCodes();
          showBackupCodesModal(codes);
        } catch (e) { alert(e.message); }
      };
    } else {
      host.innerHTML = `
        <div style="padding:12px;border:1px solid #3f3f46;background:rgba(250,204,21,0.04);border-radius:8px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:#eab308;margin-bottom:4px;">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M10 6v4M10 14h0M4 17h12a2 2 0 001.732-3L11.732 4a2 2 0 00-3.464 0L2.268 14A2 2 0 004 17z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Two-Factor Authentication is not enabled
          </div>
          <div style="font-size:12px;color:#a1a1aa;">We strongly recommend enabling 2FA to protect your account.</div>
        </div>
        <button class="btn-primary" id="btn-mfa-enroll">Enable 2FA</button>
      `;
      document.getElementById('btn-mfa-enroll').onclick = startEnrollment;
    }
  } catch (e) {
    host.innerHTML = `<div class="settings-note" style="color:#dc2626;">Could not load 2FA status: ${e.message}</div>`;
  }
}

// ─── Enrollment flow ──────────────────────────────────────────
let _pendingFactor = null;

async function startEnrollment() {
  const modal = document.getElementById('mfa-enroll-modal');
  const qrHost = document.getElementById('mfa-qr-container');
  const secretHost = document.getElementById('mfa-secret-code');
  const codeInput = document.getElementById('mfa-verify-code');
  const errorEl = document.getElementById('mfa-verify-error');

  qrHost.innerHTML = '<div style="padding:40px;color:#71717a;font-size:13px;">Generating QR code…</div>';
  secretHost.textContent = '';
  codeInput.value = '';
  errorEl.classList.add('hidden');
  modal.classList.remove('hidden');

  try {
    const { data: existing } = await getSupabase().auth.mfa.listFactors();
    const stale = (existing?.totp || []).filter(f => f.status !== 'verified');
    for (const f of stale) {
      await getSupabase().auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await getSupabase().auth.mfa.enroll({ factorType: 'totp', friendlyName: 'FitOps Pro' });
    if (error) throw error;
    _pendingFactor = { factorId: data.id, secret: data.totp.secret };
    const qr = data.totp.qr_code || '';
    qrHost.innerHTML = '';
    if (qr.trim().startsWith('<svg')) {
      // Raw SVG XML — safe to inject as innerHTML
      qrHost.innerHTML = qr;
    } else if (qr) {
      // Data URL or image URL — use DOM so quotes in the URL can't break out
      const img = document.createElement('img');
      img.src = qr;
      img.alt = 'QR Code';
      img.style.width = '200px';
      img.style.height = '200px';
      qrHost.appendChild(img);
    }
    secretHost.textContent = data.totp.secret;
    codeInput.focus();
  } catch (e) {
    qrHost.innerHTML = `<div style="padding:20px;color:#dc2626;font-size:13px;">${e.message}</div>`;
  }
}

async function verifyEnrollment() {
  const code = document.getElementById('mfa-verify-code').value.trim();
  const errorEl = document.getElementById('mfa-verify-error');
  const btn = document.getElementById('mfa-enroll-verify');
  errorEl.classList.add('hidden');
  if (!_pendingFactor) { errorEl.textContent = 'No pending enrollment. Close and retry.'; errorEl.classList.remove('hidden'); return; }
  if (!/^\d{6}$/.test(code)) { errorEl.textContent = 'Enter the 6-digit code.'; errorEl.classList.remove('hidden'); return; }

  btn.disabled = true;
  btn.textContent = 'Verifying…';
  try {
    const { data: challengeData, error: challengeError } = await getSupabase().auth.mfa.challenge({ factorId: _pendingFactor.factorId });
    if (challengeError) throw challengeError;
    const { error: verifyError } = await getSupabase().auth.mfa.verify({
      factorId: _pendingFactor.factorId, challengeId: challengeData.id, code,
    });
    if (verifyError) throw verifyError;
    _pendingFactor = null;
    document.getElementById('mfa-enroll-modal').classList.add('hidden');

    // Generate backup codes immediately — this is the first + only time the
    // user sees them.
    try {
      const codes = await generateBackupCodes();
      showBackupCodesModal(codes);
    } catch (e) {
      alert(`2FA is enabled, but we couldn't generate backup codes: ${e.message}. You can regenerate them from Settings → Security.`);
      await renderMfaStatus();
    }
  } catch (e) {
    errorEl.textContent = e.message?.includes('Invalid') ? 'Invalid code — check your authenticator app and try again.' : e.message;
    errorEl.classList.remove('hidden');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Verify & Enable';
  }
}

function closeEnrollModal() {
  document.getElementById('mfa-enroll-modal').classList.add('hidden');
  if (_pendingFactor) {
    getSupabase().auth.mfa.unenroll({ factorId: _pendingFactor.factorId }).catch(() => {});
    _pendingFactor = null;
  }
}

// ─── Disable flow ─────────────────────────────────────────────
let _disableFactorId = null;
function openDisableModal(factorId) {
  _disableFactorId = factorId;
  const modal = document.getElementById('mfa-disable-modal');
  document.getElementById('mfa-disable-code').value = '';
  document.getElementById('mfa-disable-error').classList.add('hidden');
  modal.classList.remove('hidden');
  document.getElementById('mfa-disable-code').focus();
}

async function confirmDisable() {
  const code = document.getElementById('mfa-disable-code').value.trim();
  const errorEl = document.getElementById('mfa-disable-error');
  const btn = document.getElementById('mfa-disable-confirm');
  errorEl.classList.add('hidden');
  if (!_disableFactorId) { errorEl.textContent = 'No factor selected.'; errorEl.classList.remove('hidden'); return; }
  if (!/^\d{6}$/.test(code)) { errorEl.textContent = 'Enter the 6-digit code.'; errorEl.classList.remove('hidden'); return; }

  btn.disabled = true;
  btn.textContent = 'Disabling…';
  try {
    const { data: challengeData, error: challengeError } = await getSupabase().auth.mfa.challenge({ factorId: _disableFactorId });
    if (challengeError) throw challengeError;
    const { error: verifyError } = await getSupabase().auth.mfa.verify({
      factorId: _disableFactorId, challengeId: challengeData.id, code,
    });
    if (verifyError) throw verifyError;
    const { error: unenrollError } = await getSupabase().auth.mfa.unenroll({ factorId: _disableFactorId });
    if (unenrollError) throw unenrollError;
    _disableFactorId = null;
    document.getElementById('mfa-disable-modal').classList.add('hidden');
    await renderMfaStatus();
  } catch (e) {
    errorEl.textContent = e.message?.includes('Invalid') ? 'Invalid code.' : e.message;
    errorEl.classList.remove('hidden');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Disable 2FA';
  }
}

// ─── Admin enforcement check ──────────────────────────────────
// On dashboard load: if platform requires MFA for coaches AND this
// coach has no verified TOTP, force them into enrollment.
async function enforceMfaIfRequired() {
  try {
    // platform_config is service-role-only after the security sprint,
    // so we route the lookup through a Netlify function that holds
    // service_role and just returns the boolean. Avoids console-spam
    // 403s for non-owner coaches.
    const { data: { session } } = await getSupabase().auth.getSession();
    if (!session?.access_token) return;
    const res = await fetch('/.netlify/functions/get-mfa-requirement', {
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    });
    if (!res.ok) return; // fail-closed to not blocking the UI
    const { required } = await res.json();
    if (!required) return;

    const { data } = await getSupabase().auth.mfa.listFactors();
    const hasVerified = (data?.totp || []).some(f => f.status === 'verified');
    if (hasVerified) return;

    // Force enrollment — block the rest of the UI until they complete
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(9,9,11,0.92);display:flex;align-items:center;justify-content:center;z-index:10000;';
    overlay.innerHTML = `
      <div style="max-width:440px;padding:32px;background:#18181b;border:1px solid #27272a;border-radius:12px;text-align:center;">
        <h2 style="margin:0 0 12px;color:#fafafa;">Two-Factor Authentication Required</h2>
        <p style="margin:0 0 20px;color:#a1a1aa;font-size:14px;line-height:1.5;">Your account owner has required all coaches to enable 2FA before accessing the CRM. Please set it up now to continue.</p>
        <button id="force-mfa-start" class="btn-primary" style="padding:12px 32px;">Set Up 2FA</button>
      </div>`;
    document.body.appendChild(overlay);
    document.getElementById('force-mfa-start').onclick = async () => {
      // Ensure the Settings view + Security tab are visible, then open enroll
      document.querySelectorAll('.view').forEach(v => v.classList.add('hidden'));
      document.getElementById('view-settings')?.classList.remove('hidden');
      document.querySelectorAll('.settings-tab-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.settings-tab-panel').forEach(p => p.classList.remove('active'));
      document.querySelector('[data-tab="security"]')?.classList.add('active');
      document.getElementById('settings-tab-security')?.classList.add('active');
      overlay.remove();
      await renderMfaStatus();
      await startEnrollment();
      // Re-check after enrollment completes
      const recheckInterval = setInterval(async () => {
        const { data: f } = await getSupabase().auth.mfa.listFactors();
        if ((f?.totp || []).some(x => x.status === 'verified')) {
          clearInterval(recheckInterval);
        } else if (!document.getElementById('mfa-enroll-modal')?.classList.contains('hidden')) {
          // enrollment modal still open — let it finish
        } else {
          // Modal closed without success — re-block
          clearInterval(recheckInterval);
          location.reload();
        }
      }, 2000);
    };
  } catch (e) {
    console.warn('[mfa enforce]', e.message);
  }
}

// ─── Wire up modal event handlers ─────────────────────────────
document.getElementById('mfa-enroll-close')?.addEventListener('click', closeEnrollModal);
document.getElementById('mfa-enroll-cancel')?.addEventListener('click', closeEnrollModal);
document.getElementById('mfa-enroll-verify')?.addEventListener('click', verifyEnrollment);
document.getElementById('mfa-disable-close')?.addEventListener('click', () => document.getElementById('mfa-disable-modal').classList.add('hidden'));
document.getElementById('mfa-disable-cancel')?.addEventListener('click', () => document.getElementById('mfa-disable-modal').classList.add('hidden'));
document.getElementById('mfa-disable-confirm')?.addEventListener('click', confirmDisable);
document.getElementById('mfa-verify-code')?.addEventListener('keydown', e => { if (e.key === 'Enter') verifyEnrollment(); });
document.getElementById('mfa-disable-code')?.addEventListener('keydown', e => { if (e.key === 'Enter') confirmDisable(); });

// Re-render when Security tab becomes active
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-tab="security"]');
  if (btn) renderMfaStatus();
});

// Defer boot-time calls past app.js's static-init phase. mfa.js is
// statically imported by app.js, so its module body runs BEFORE app.js
// reaches `_config.setSupabaseRef(...)`. queueMicrotask pushes these
// calls onto the microtask queue, which drains AFTER the current task
// (app.js's synchronous initialization) completes — by then the
// singleton is registered and getSupabase() returns the live client.
queueMicrotask(() => {
  if (document.getElementById('settings-tab-security')?.classList.contains('active')) {
    renderMfaStatus();
  }
  // Run enforcement check on dashboard load (async, non-blocking for UI)
  enforceMfaIfRequired();
});

export { renderMfaStatus };
